import { useEffect, useState } from 'react';

import {
  countActiveBuyerRequests,
  countDistinctDealsViewed7d,
  countProcessedBuyerRequests,
  countSavedDeals,
} from '@/lib/db/buyer';

// ---------------------------------------------------------------------------
// useBuyerDashboardMetrics — the 4 dashboard KPIs (Spec §M7 L908-915)
// ---------------------------------------------------------------------------
// Each KPI is a dedicated COUNT (head:true) or a bounded distinct count — never
// a materialised, paginated row list. If any count rejects (e.g. a column
// drift), the whole band goes to 'error' rather than silently showing zeros
// (memory feedback_supabase_column_drift — Promise.all must not mask a 400).

export type DashboardMetrics = {
  saved: number;
  active: number;
  processed: number;
  views: number;
};

export type DashboardMetricsState =
  | { status: 'loading'; metrics: null }
  | { status: 'success'; metrics: DashboardMetrics }
  | { status: 'error'; metrics: null };

export function useBuyerDashboardMetrics(userId: string | null): DashboardMetricsState {
  const [state, setState] = useState<DashboardMetricsState>({ status: 'loading', metrics: null });

  useEffect(() => {
    if (!userId) {
      setState({ status: 'success', metrics: { saved: 0, active: 0, processed: 0, views: 0 } });
      return;
    }

    let cancelled = false;
    setState({ status: 'loading', metrics: null });

    Promise.all([
      countSavedDeals(userId),
      countActiveBuyerRequests(),
      countProcessedBuyerRequests(),
      countDistinctDealsViewed7d(),
    ])
      .then(([saved, active, processed, views]) => {
        if (cancelled) return;
        setState({ status: 'success', metrics: { saved, active, processed, views } });
      })
      .catch(() => {
        if (cancelled) return;
        setState({ status: 'error', metrics: null });
      });

    return () => {
      cancelled = true;
    };
  }, [userId]);

  return state;
}
